import { CssBaseline } from "@mui/material";
import { useEffect, useState } from "react";
import { galleryFixtures, galleryQuestionRequest } from "../gallery/catalog";
import {
  GlobalProvider,
  useThemeContext,
} from "../globalContext/global-context";
import {
  questionRegistry,
  supportsSurveyQuestion,
} from "../questions/registry";
import { SurveySession } from "../session/SurveySession";
import type { SurveyTransport } from "../session/transport";
import { SurveyAppShell } from "../shell/SurveyAppShell";
import { buildSurveyTheme } from "../theme/themeFunctions/buildSurveyTheme";
import type { Question } from "../util/types";
import {
  createThemePreviewReadyMessage,
  getAdminUiOrigin,
  isThemePreviewQuestionMessage,
  isThemePreviewUpdateMessage,
} from "./protocol";

const previewFixtures = galleryFixtures.filter(
  (fixture, index, fixtures) =>
    fixtures.findIndex((candidate) => candidate.type === fixture.type) ===
    index,
);

const questionTypeLabel = (type: string) =>
  type
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const questionTypes = previewFixtures.map((fixture) => ({
  label: questionTypeLabel(fixture.type),
  value: fixture.type,
}));

export const createPreviewSession = (questionType: string) => {
  const fixture = previewFixtures.find(
    (candidate) => candidate.type === questionType,
  )!;
  const view =
    questionRegistry.get(fixture.type)?.defaultDisplay.view ?? "default";

  const question = {
    question: galleryQuestionRequest(fixture, view, false),
    respondent_id: "theme-preview",
    unique_id: `theme-preview-${fixture.type}`,
    pct_progress: 0.6,
    sequence_id: 0,
  } as unknown as Question;
  const transport: SurveyTransport = {
    request: async (command) => (command.kind === "load" ? question : null),
  };
  return new SurveySession(transport, undefined, supportsSurveyQuestion);
};

const ThemePreview = () => {
  const { updateGlobalObject } = useThemeContext();
  const [questionType, setQuestionType] = useState<string>(
    previewFixtures[0].type,
  );
  const [session, setSession] = useState<SurveySession | null>(null);

  useEffect(() => {
    const nextSession = createPreviewSession(questionType);
    setSession(nextSession);
    nextSession.start();
    return () => nextSession.dispose();
  }, [questionType]);

  useEffect(() => {
    const adminOrigin = getAdminUiOrigin();
    if (!adminOrigin || window.parent === window) return;

    const receivePreviewMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== adminOrigin || event.source !== window.parent) {
        return;
      }

      const message = event.data;
      if (isThemePreviewUpdateMessage(message)) {
        const { normalizedTheme, legacyTheme } = buildSurveyTheme(
          message.theme,
        );
        updateGlobalObject((current) => ({
          ...current,
          themeData: { normalizedTheme, theme: legacyTheme },
        }));
        return;
      }

      if (
        isThemePreviewQuestionMessage(message) &&
        previewFixtures.some((fixture) => fixture.type === message.questionType)
      ) {
        setQuestionType(message.questionType);
      }
    };
    window.addEventListener("message", receivePreviewMessage);
    window.parent.postMessage(
      createThemePreviewReadyMessage(questionTypes),
      adminOrigin,
    );
    return () => window.removeEventListener("message", receivePreviewMessage);
  }, [updateGlobalObject]);

  return session ? <SurveyAppShell session={session} /> : null;
};

export const ThemePreviewFrame = () => (
  <GlobalProvider offline>
    <CssBaseline />
    <ThemePreview />
  </GlobalProvider>
);
